import { BellRing } from "lucide-react";
import { useState } from "react";
import { ApiError, post, type Me, type Member, type Trip } from "../lib/api.ts";
import { formatBytes } from "../lib/transfer.ts";
import { isAdmin } from "../lib/rights.ts";
import { useApi } from "../lib/useApi.ts";
import { InviteStep } from "./CreateTripPage.tsx";
import { Card, ICON, Metric, NestedCard, PageState, PersonRow, reason } from "../ui/index.ts";

export type Participant = {
  userId: string;
  displayName: string;
  deviceId: string;
  lastScanAt: string;
  stale: boolean;
  coverage: { source: string; status: string }[];
  counts: { discovered: number; verified: number; failed: number };
  bytes: { discovered: string; pending: string; verified: string };
};
export type SyncStatusDto = {
  participants: Participant[];
  totals: { discovered: string; pending: string; verified: string };
  coverage: { externalCardNotScanned: number };
};

export const ago = (iso: string) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 60
    ? `${m} min ago`
    : m < 2880
      ? `${Math.round(m / 60)} h ago`
      : `${Math.round(m / 1440)} days ago`;
};

/** One summary per contributor across their devices; null when they have not scanned at all. */
export function summarize(members: Member[], s: SyncStatusDto) {
  return members
    .filter((m) => m.roles.includes("CONTRIBUTOR"))
    .map((m) => {
      const mine = s.participants.filter((p) => p.userId === m.userId);
      if (mine.length === 0) return { member: m, scan: null };
      const sum = (k: "discovered" | "pending" | "verified") =>
        mine.reduce((a, p) => a + Number(p.bytes[k]), 0);
      const last = mine
        .map((p) => p.lastScanAt)
        .sort()
        .at(-1)!;
      return {
        member: m,
        scan: {
          lastScanAt: last,
          stale: mine.every((p) => p.stale),
          discovered: sum("discovered"),
          pending: sum("pending"),
          verified: sum("verified"),
          cardNotScanned: mine.some((p) =>
            p.coverage.some((c) => c.source === "EXTERNAL_CARD" && c.status === "NOT_SCANNED"),
          ),
          failed: mine.reduce((a, p) => a + p.counts.failed, 0),
        },
      };
    });
}

const DEFAULT_MESSAGE = "We are waiting for your footage. Can you sync when you have Wi-Fi?";

/** F. Shared sync status and G. sync nudges. */
export function PeoplePage({ trip, me }: { trip: Trip; me: Me }) {
  const sync = useApi<SyncStatusDto>(`/v1/trips/${trip.id}/sync-status`, { pollMs: 15_000 });
  const members = useApi<{ items: Member[] }>(`/v1/trips/${trip.id}/members`);
  const [nudging, setNudging] = useState<Member | null>(null);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function send() {
    if (!nudging) return;
    setBusy(true);
    try {
      await post(`/v1/trips/${trip.id}/nudges`, { recipientId: nudging.userId, message });
      setResult(`Reminder sent to ${nudging.displayName}.`);
      setNudging(null);
    } catch (e) {
      setResult(
        e instanceof ApiError && e.status === 429
          ? `You already reminded ${nudging.displayName} recently. You can send another later.`
          : reason(e as Error),
      );
    } finally {
      setBusy(false);
    }
  }

  const data =
    sync.data && members.data
      ? { s: sync.data, rows: summarize(members.data.items, sync.data) }
      : null;
  return (
    <PageState
      data={data}
      error={sync.error ?? members.error}
      loading={sync.loading || members.loading}
      reload={() => {
        void sync.reload();
        void members.reload();
      }}
    >
      {({ s, rows }) => {
        const notScanned = rows.filter((r) => r.scan === null).length;
        return (
          <div className="stack">
            <Card>
              <div className="metrics">
                <Metric value={formatBytes(Number(s.totals.verified))} caption="Verified" />
                <Metric value={formatBytes(Number(s.totals.pending))} caption="Pending" />
                <Metric value={String(notScanned)} caption="Not scanned yet" />
              </div>
            </Card>
            <Card title="Contributors">
              {rows.length === 0 && (
                <p className="muted">No contributors yet. Invite travellers from the overview.</p>
              )}
              {rows.map(({ member, scan }) => (
                <div key={member.userId}>
                  <PersonRow
                    name={member.displayName}
                    lastScan={scan ? ago(scan.lastScanAt) : null}
                    verified={formatBytes(scan?.verified ?? 0)}
                    discovered={scan ? formatBytes(scan.discovered) : null}
                    pending={scan ? formatBytes(scan.pending) : null}
                    progress={scan && scan.discovered > 0 ? scan.verified / scan.discovered : null}
                  />
                  {scan && (scan.cardNotScanned || scan.stale || scan.failed > 0) && (
                    <p className="muted small" style={{ margin: "0 0 8px 48px" }}>
                      {[
                        scan.cardNotScanned && "Camera card not scanned",
                        scan.stale && "No update for over a day",
                        scan.failed > 0 && `${scan.failed} need attention`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  )}
                  {member.userId !== me.user.id && (!scan || scan.pending > 0 || scan.stale) && (
                    <div style={{ margin: "0 0 8px 48px" }}>
                      <button
                        className="button small"
                        type="button"
                        onClick={() => {
                          setNudging(member);
                          setResult(null);
                        }}
                      >
                        <BellRing {...ICON} aria-hidden="true" />
                        Remind {member.displayName}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </Card>
            {nudging && (
              <Card title={`Reminder for ${nudging.displayName}`}>
                <NestedCard>
                  <label className="field">
                    <span>Message</span>
                    <textarea
                      rows={3}
                      maxLength={500}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </label>
                </NestedCard>
                <div className="row">
                  <button className="button" type="button" onClick={() => setNudging(null)}>
                    Cancel
                  </button>
                  <span className="spacer" />
                  <button
                    className="button primary"
                    type="button"
                    onClick={send}
                    disabled={busy || !message.trim()}
                  >
                    Send reminder
                  </button>
                </div>
              </Card>
            )}
            {result && (
              <p role="status" className="muted">
                {result}
              </p>
            )}
            {isAdmin(trip.roles) && <InviteStep trip={trip} showGoToTrip={false} />}
          </div>
        );
      }}
    </PageState>
  );
}
